<template>
    <v-container class="pa-5" fluid>
        <GenerateBillboardLabelsDialog
            :is-open="generateDialogLabelsOpen"
            @on-loaded="xlsDataLoaded"
            @on-close="closeGenerateDialogLabels"
        />
        <ConfirmDialog
            removal
            :is-open="updateDataDialogOpen"
            @on-confirm="confirmUpdateDataDialog"
            @on-abort="cancelUpdateDataDialog"
        >
            <template #title>
                Zmiana klienta
            </template>
            <template #content>
                Czy na pewno zmienić klienta? Wszystkie zaimportowane dane zostaną utracone.
            </template>
            <template #confirmButton>
                Kontynuuj
            </template>
        </ConfirmDialog>
        <ConfirmDialog
            removal
            :is-open="printConfirmationDialogOpen"
            @on-confirm="confirmPrintDialog"
            @on-abort="cancelPrintDialog"
        >
            <template #title>
                Wydruk wielu etykiet
            </template>
            <template #content>
                Uwaga! Liczba etykiet do wydruku - <strong>{{ labelsToPrint.length }}</strong> szt.
            </template>
            <template #confirmButton>
                Kontynuuj
            </template>
        </ConfirmDialog>
        <ConfirmDialog
            removal
            :is-open="reloadDataDialogOpen"
            @on-confirm="confirmReloadDataDialog"
            @on-abort="cancelReloadDataDialog"
        >
            <template #title>
                Ponowny import danych
            </template>
            <template #content>
                Czy na pewno chcesz ponownie zaimportować dane? Wszystkie dotychczas zaimportowane zostaną utracone.
            </template>
            <template #confirmButton>
                Kontynuuj
            </template>
        </ConfirmDialog>
        <WorkspaceSettingsDialog
            :is-open="workspaceSettingsDialog"
            :workspace-id-data="workspaceId"
            :workspaces="workspaces"
            @on-save="saveWorkspaceSettings"
            @on-abort="closeWorkspaceSettingsDialog"
        />

        <v-row dense>
            <v-col cols="auto">
                <page-title>Generator etykiet billboardowych</page-title>
            </v-col>
        </v-row>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót
                </v-btn>
            </v-col>
        </v-row>
        <v-row justify="space-between">
            <v-col cols="12" md="2" sm="5">
                <v-select
                    v-model="customerModel"
                    label="Klient"
                    :items="availableCustomers"
                    item-value="id"
                    item-text="codeName"
                    no-data-text="Nie znaleziono klientów"
                    solo
                    hide-details
                    return-object
                    @change="beforeCustomerChanged"
                />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="4" xl="3">
                <v-card>
                    <v-toolbar flat dense color="grey lighten-4">
                        <v-container class="pa-0">
                            <v-row justify="space-between" dense align="center">
                                <v-col>
                                    <v-toolbar-title class="text-subtitle-1">
                                        Stanowisko
                                    </v-toolbar-title>
                                </v-col>
                                <v-col cols="auto">
                                    <v-tooltip :open-delay="tooltipDelay" top>
                                        <template #activator="{on}">
                                            <v-btn
                                                v-if="canChangeWorkspace"
                                                fab
                                                x-small
                                                color="secondary"
                                                elevation="1"
                                                :loading="actionLoading.workspaces"
                                                v-on="on"
                                                @click="showWorkspaceSettingsDialog(false)"
                                            >
                                                <v-icon>
                                                    mdi-printer-3d
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Zmiana stanowiska</span>
                                    </v-tooltip>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-toolbar>

                    <v-divider />

                    <v-card-text class="py-8 text-center">
                        <div class="text-h3 font-weight-bold black--text">
                            {{ currentWorkspace ? currentWorkspace.name : '-' }}
                        </div>
                    </v-card-text>
                    <v-col cols="12">
                        <v-select
                            v-model="printerId"
                            :items="printers"
                            item-value="id"
                            item-text="name"
                            label="Drukarka"
                            hide-details
                            no-data-text="Nie znaleziono drukarki"
                            outlined
                            dense
                        />
                    </v-col>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="auto">
                <v-btn
                    color="secondary"
                    medium
                    class="mr-4"
                    data-sentry-label="Importuj dane"
                    :diabled="!currentCustomer"
                    @click="beforeShowGenerateLabelsDialog()"
                >
                    <v-icon left>
                        mdi-file-upload-outline
                    </v-icon>
                    Importuj dane
                </v-btn>
            </v-col>
            <v-spacer />
            <v-col cols="auto">
                <v-spacer />
                <v-btn
                    color="primary"
                    medium
                    data-sentry-label="Drukuj zaznaczone etykiety"
                    :disabled="selected.length === 0"
                    @click="printSelectedLabels(selected)"
                >
                    <v-icon left>
                        mdi-printer
                    </v-icon>
                    Drukuj zaznaczone etykiety
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card-title>
                    <v-spacer />
                    <v-text-field
                        v-model="search"
                        append-icon="mdi-magnify"
                        label="Szukaj"
                        single-line
                        hide-details
                        :disabled="items.length === 0"
                        clearable
                        @input="updateSearch"
                    />
                </v-card-title>
                <v-data-table
                    ref="dataTable"
                    v-model="selected"
                    :items="items"
                    :headers="mainHeaders"
                    multi-sort
                    show-expand
                    single-expand
                    show-select
                    item-key="id"
                    :expanded.sync="expanded"
                    :search="search"
                    @pagination="clearSelection()"
                    @update:sort-by="setSortedBy"
                    @update:sort-desc="setSortedDesc"
                >
                    <template #item.routeNumber="{item}">
                        {{ item.routeNumber }}
                    </template>
                    <template #item.commercialTerm="{item}">
                        {{ item.commercialTerm }}
                    </template>
                    <template #item.productName="{item}">
                        {{ item.productName }}
                    </template>
                    <template #item.numberOfSets="{item}">
                        {{ item.numberOfSets }}
                    </template>
                    <template #item.actions="{item}">
                        <div class="d-inline-flex">
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Wydrukuj etykiety"
                                        :disabled="!printerId"
                                        v-on="on"
                                        @click="printLabels(item.labels)"
                                    >
                                        <v-icon color="blue">
                                            mdi-printer
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Drukuj etykiety</span>
                            </v-tooltip>
                        </div>
                    </template>
                    <template #expanded-item="{headers, item}">
                        <td :colspan="headers.length">
                            <v-row justify="end">
                                <v-col cols="auto">
                                    <strong>Etykiety</strong>
                                </v-col>
                                <v-spacer />
                                <v-col cols="7">
                                    <v-data-table
                                        :items="item.labels"
                                        :headers="labelsHeaders"
                                        disable-pagination
                                        :mobile-breakpoint="0"
                                        item-key="boxNumbering"
                                        hide-default-footer
                                        dense
                                    >
                                        <template #item.boxNumbering="{item: labelItem}">
                                            {{ labelItem.boxNumbering }}
                                        </template>
                                        <template #item.numberOfSets="{item: labelItem}">
                                            {{ labelItem.numberOfSets }}
                                        </template>
                                        <template #item.labelActions="{item: labelItem}">
                                            <div class="d-inline-flex">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Wydrukuj etykiety"
                                                            :disabled="!printerId"
                                                            v-on="on"
                                                            @click="printLabels([labelItem])"
                                                        >
                                                            <v-icon color="blue">
                                                                mdi-printer
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Drukuj etykietę</span>
                                                </v-tooltip>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </td>
                    </template>

                    <template slot="no-data">
                        <p class="text-center mt-4">
                            Brak danych
                            <v-btn
                                color="secondary"
                                class="mr-4"
                                small
                                text
                                data-sentry-label="Importuj"
                                @click="showGenerateLabelsDialog()"
                            >
                                <v-icon left>
                                    mdi-file-upload-outline
                                </v-icon>
                                Importuj
                            </v-btn>
                        </p>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import router from '@/router'
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import GenerateBillboardLabelsDialog from './GenerateBillboardLabelsDialog'
import WorkspaceSettingsDialog from './WorkspaceSettingsDialog'
import tooltipDelay from '@/mixins/tooltip'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'BillboardLabels',
    components: {
        PageTitle,
        GenerateBillboardLabelsDialog,
        ConfirmDialog,
        WorkspaceSettingsDialog
    },
    mixins: [
        tooltipDelay
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('customers/getCustomers', {
                paginate: '',
                query: '?hasBillboardLabel=true'
            }),
            store.dispatch('configurations/getProductionSites', {query:'?type_id=6'})
        ])
            .then(responses => {
                next(vm => {
                    vm.availableCustomers = responses[0].data.results
                    vm.currentCustomer = vm.availableCustomers[0]
                    const workspace = JSON.parse(localStorage.getItem('billboardLabelsWorkspace'))

                    if (!workspace) {
                        vm.showWorkspaceSettingsDialog()
                    } else {
                        vm.workspaceId = workspace.workspaceId
                        vm.setBillboardLabelsSettings(vm.workspaceId)
                    }
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            search: '',
            expanded: [],
            selected: [],
            availableCustomers: [],
            customer: null,
            currentCustomer: null,
            generateDialogLabelsOpen: false,
            updateDataDialogOpen : false,
            printConfirmationDialogOpen: false,
            reloadDataDialogOpen: false,
            labelsToPrint: [],
            xlsData: null,
            fileName: null,
            items: [],
            sortBy: [],
            descSort: [],
            settings : {
                workspace: null
            },
            actionLoading: {
                workspaces: false
            },
            workspaceId: null,
            workspaceSettingsDialog: false,
            printerId: null,
            mainHeaders: [
                {
                    text: 'Trasa',
                    value: 'routeNumber',
                    align: 'left',
                    sortable: true
                },
                {
                    text: 'Ekipa',
                    value: 'commercialTerm',
                    align: 'center',
                    sortable: true
                },
                {
                    text: 'Produkt',
                    value: 'productName',
                    align: 'left',
                    sortable: true
                },
                {
                    text: 'Ilość kompletów',
                    value: 'numberOfSets',
                    align: 'right',
                    sortable: false
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'right',
                    sortable: false,
                    width: 100,
                    showOnReadOnly: false
                },
                {
                    text: '',
                    value: 'data-table-expand',
                    showOnRead: true
                }
            ],
            labelsHeaders: [
                {
                    text: 'Nr etykiety',
                    value: 'boxNumbering',
                    align: 'center',
                    sortable: false
                },
                {
                    text: 'Ilość kompletów',
                    value: 'numberOfSets',
                    align: 'center',
                    sortable: false
                },
                {
                    text: 'Akcje',
                    value: 'labelActions',
                    align: 'right',
                    sortable: false,
                    width: 100,
                },
            ]
        }
    },
    computed: {
        customerModel: {
            get() {
                return this.availableCustomers.length > 0 && !this.customer ? this.availableCustomers[0] : this.customer
            },
            set(value) {
                this.customer = value
            }
        },
        workspaces() {
            return this.$store.state.configurations.productionSites.productionSites
        },
        currentWorkspace() {
            return this.billboardLabelsSettings?.workspace
        },
        printers() {
            if (this.currentWorkspace) {
                return this.currentWorkspace.printers
            }

            return []
        },
        billboardLabelsSettings() {
            return this.$store.state.shipments.labelsGenerator.labelsGenerator.billboardLabelsSettings
        },
        canChangeWorkspace() {
            return hasUserPermission('production_sites_admin_update')
        }
    },
    watch: {
        search() {
            this.selected = []
        }
    },
    methods: {
        setDefaultPrinter(workspace) {
            if (workspace) {
                if (workspace.printerId) {
                    return this.printerId = workspace.printerId
                } else if (workspace.printers.length > 0) {
                    return this.printerId = workspace.printers[0].id
                }
            }

            return null
        },
        setBillboardLabelsSettings(workspaceId) {
            this.getWorkspace(workspaceId).then(response => {
                this.workspaceId = response.data.id
                localStorage.setItem('billboardLabelsWorkspace', JSON.stringify({
                    'workspaceId': workspaceId,
                }))
                this.settings.workspace = response.data
                store.dispatch('shipments/labelsGenerator/setBillboardLabelsSettings', this.settings)
                this.setDefaultPrinter(this.settings.workspace)
            })
        },
        async getWorkspace(workspaceId) {
            return await this.$store.dispatch('configurations/getProductionSite', workspaceId)
                .catch(error => {
                    errorHandler(error)
                })
        },
        showWorkspaceSettingsDialog() {
            this.actionLoading.workspaces = true
            addSentryBreadcrumb('ui.action', 'Ustawienia stanowiska')

            this.$store.dispatch('configurations/getProductionSites', {query:'?type_id=6'})
                .then(() => {
                    this.workspaceSettingsDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.workspaces = false
                })
        },
        closeWorkspaceSettingsDialog() {
            this.workspaceSettingsDialog = false
        },
        saveWorkspaceSettings(value) {
            this.setBillboardLabelsSettings(value)
            this.closeWorkspaceSettingsDialog()
        },
        setSortedBy(sortedBy) {
            this.sortBy = sortedBy
        },
        setSortedDesc(sortedDesc) {
            this.descSort = sortedDesc
        },
        getSortConditionsObject() {
            let result = []

            this.sortBy.forEach((element, index) => {
                const obj = {
                    name: element,
                    descending: this.descSort[index]
                }

                result.push(obj)
            })

            return result
        },
        sortObjectsByConditions(objects, conditions) {

            objects.sort((a, b) => {
                for (let i = 0; i < conditions.length; i++) {
                    const condition = conditions[i]
                    const field = condition.name
                    const descending = condition.descending
                    let firstValue = a[field] 
                    let secondValue = b[field]

                    if (typeof a[field] === 'string' || typeof b[field] === 'string') {
                        firstValue = a[field]?.toString()
                        secondValue = b[field]?.toString()

                    }
                    const comparison = this.compareValues(firstValue, secondValue)

                    if (comparison !== 0) {
                        return descending ? -comparison : comparison
                    }
                }

                return 0 // Objects are equal
            })

            return objects
        },
        compareValues(a, b) {
            if (a < b) {
                return -1
            } else if (a > b) {
                return 1
            } else {
                return 0
            }
        },
        clearSelection() {
            this.selected = []
        },
        updateSearch(value) {
            this.search = value
            this.$refs.dataTable.selected = []
        },
        beforeCustomerChanged(customer) {
            if (this.items.length > 0) {
                this.updateDataDialogOpen = true
            } else {
                this.changeCustomer(customer)
            }
        },
        changeCustomer(customer) {
            this.currentCustomer = customer
            this.items = []
        },
        cancelReloadDataDialog() {
            this.reloadDataDialogOpen = false
        },
        confirmReloadDataDialog() {
            this.search = ''
            this.items = []
            this.generateDialogLabelsOpen = true
            this.reloadDataDialogOpen = false
        },
        beforeShowGenerateLabelsDialog() {
            if (this.items.length > 0) {
                this.reloadDataDialogOpen = true
            } else {
                this.showGenerateLabelsDialog()
            }
        },
        showGenerateLabelsDialog() {
            this.generateDialogLabelsOpen = true
        },
        closeGenerateDialogLabels() {
            this.generateDialogLabelsOpen = false
        },
        confirmUpdateDataDialog() {
            this.search = ''
            this.items = []
            this.updateDataDialogOpen = false
        },
        cancelUpdateDataDialog() {
            this.updateDataDialogOpen = false
        },
        showPrintConfirmationDialog() {
            this.printConfirmationDialogOpen = true
        },
        printSelectedLabels(selected) {
            this.labelsToPrint = selected.flatMap(obj => obj.labels)

            if (this.labelsToPrint.length > 1) {
                const sortConditions = this.getSortConditionsObject()

                if (sortConditions.length > 0) {
                    this.labelsToPrint = this.sortObjectsByConditions(this.labelsToPrint, sortConditions)
                }
            }

            if (this.labelsToPrint.length >= 15) {
                this.printConfirmationDialogOpen = true
            } else {
                this.printLabels(this.labelsToPrint)
            }
        },
        confirmPrintDialog() {
            this.printLabels(this.labelsToPrint)
            this.labelsToPrint = []
            this.printConfirmationDialogOpen = false
        },
        cancelPrintDialog() {
            this.printConfirmationDialogOpen = false
        },
        printLabels(labelsData) {
            this.$store.dispatch('shipments/sendings/setGenerateBillboardLabels', {
                billboardLabels: labelsData,
                shop: this.currentCustomer.name,
                fileName: this.fileName,
                printerId: this.printerId
            })
        },
        xlsDataLoaded({data, fileName}) {
            this.xlsData = data.filter(i => i.length > 0)
            this.fileName = fileName

            this.generateTableItems()
        },
        generateTableItems() {
            if (this.xlsData) {
                const data = this.xlsData
                const keys = data[0]
                const objects = []
                let index = 0

                for (let i = 1; i < data.length; i++) {
                    for (let j = 2; j < keys.length; j++) {
                        if (data[i][j] !== 0) { // check if there are any products
                            const object = {}

                            object['id'] = index
                            object['routeNumber'] = data[i][0]
                            object['commercialTerm'] = data[i][1]
                            object['productName'] = keys[j]
                            object['numberOfSets'] = data[i][j]
                            object['labels'] = []

                            const productPackagesQuantities = this.divideEquallyWithMax(data[i][j], 20)

                            productPackagesQuantities.forEach((element, index) => {
                                const labelObject = {}
                                
                                labelObject['boxNumbering'] = `${index+1}/${productPackagesQuantities.length}`
                                labelObject['numberOfSets'] = element
                                labelObject['shopName'] = this.customerModel.codeName
                                labelObject['billboardLabelClientId'] = this.customerModel.id
                                labelObject['commercialTerm'] = object['commercialTerm']
                                labelObject['productName'] = object['productName']
                                labelObject['routeNumber'] = object['routeNumber']
                                object['labels'].push(labelObject)
                            })
    
                            objects.push(object)
                            index++
                        }
                    }
                }
                // Sort objects by team
                objects.sort((a, b) => {
                    const teamA = a.commercialTerm.toUpperCase()
                    const teamB = b.commercialTerm.toUpperCase()

                    return teamA.localeCompare(teamB)
                })
                this.items = objects
            }
        },
        divideEquallyWithMax(number, max) {
            const parts = Math.ceil(number / max)
            const quotient = Math.floor(number / parts) // Get the integer quotient
            const remainder = number % parts // Get the remainder

            const result = Array(parts).fill(quotient) // Fill the array with quotient

            // Distribute the remainder evenly among the parts
            for (let i = 0; i < remainder; i++) {
                result[i]++
            }

            // Adjust values greater than the maximum
            for (let i = 0; i < result.length; i++) {
                if (result[i] > max) {
                    let diff = result[i] - max
                    
                    result[i] = max

                    let j = i + 1

                    while (diff > 0 && j < result.length) {
                        const available = max - result[j]
                        const transfer = Math.min(diff, available)
                        
                        result[j] += transfer
                        diff -= transfer
                        j++
                    }
                }
            }

            // If any element is less than the quotient, distribute the remaining difference
            for (let i = 0; i < result.length; i++) {
                let diff = quotient - result[i]

                if (diff > 0) {
                    let j = i + 1

                    while (diff > 0 && j < result.length) {

                        const available = result[j] - quotient
                        const transfer = Math.min(diff, available)

                        result[i] += transfer
                        result[j] -= transfer
                        diff -= transfer
                        j++
                    }
                }
            }

            return result
        },
        close() {
            router.push({ name: 'ShippingLabelsGeneratorAll' })
                .catch(error => {
                    errorHandler(error)
                })
        }
    }
}
</script>